import React, { useCallback, useEffect, useId, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Button } from "./ui/button"
import { cn } from "../lib/utils"
import { registerAppDialogHandlers } from "../lib/app-dialogs"

const TOAST_MS = 4500

function DialogCard({ title, message, children, onBackdrop }) {
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onBackdrop?.()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "app-dialog-title" : undefined}
        aria-describedby="app-dialog-message"
        className="w-full max-w-md rounded-xl border border-border bg-card p-5 text-foreground shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {title ? (
          <h2 id="app-dialog-title" className="mb-2 text-base font-semibold">
            {title}
          </h2>
        ) : null}
        <p id="app-dialog-message" className="whitespace-pre-wrap text-sm text-foreground/90">
          {message}
        </p>
        {children}
      </div>
    </div>
  )
}

export function AppDialogProvider({ children }) {
  const [dialog, setDialog] = useState(null)
  const [toasts, setToasts] = useState([])
  const inputRef = useRef(null)
  const inputId = useId()
  const resolveRef = useRef(null)

  const closeDialog = useCallback((result) => {
    const resolve = resolveRef.current
    resolveRef.current = null
    setDialog(null)
    resolve?.(result)
  }, [])

  const openDialog = useCallback((spec) => {
    return new Promise((resolve) => {
      // Replace any open dialog
      if (resolveRef.current) resolveRef.current(spec.kind === "confirm" ? false : null)
      resolveRef.current = resolve
      setDialog(spec)
    })
  }, [])

  const pushToast = useCallback((message, options = {}) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const type = options.type === "alert" ? "alert" : "notice"
    setToasts((prev) => [...prev, { id, message, type }])
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, options.durationMs || TOAST_MS)
  }, [])

  useEffect(() => {
    registerAppDialogHandlers({
      alert: (message, options = {}) =>
        openDialog({
          kind: "alert",
          title: options.title || "Notice",
          message,
          confirmLabel: options.confirmLabel || "OK",
        }),
      confirm: (message, options = {}) =>
        openDialog({
          kind: "confirm",
          title: options.title || "Please confirm",
          message,
          confirmLabel: options.confirmLabel || "Confirm",
          cancelLabel: options.cancelLabel || "Cancel",
          danger: Boolean(options.danger),
        }),
      prompt: (message, defaultValue = "", options = {}) =>
        openDialog({
          kind: "prompt",
          title: options.title || "Input required",
          message,
          defaultValue,
          confirmLabel: options.confirmLabel || "OK",
          cancelLabel: options.cancelLabel || "Cancel",
          placeholder: options.placeholder || "",
        }),
      toast: pushToast,
    })
  }, [openDialog, pushToast])

  useEffect(() => {
    if (!dialog) return undefined
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault()
        closeDialog(dialog.kind === "confirm" ? false : null)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [dialog, closeDialog])

  useEffect(() => {
    if (dialog?.kind === "prompt") {
      const t = window.setTimeout(() => inputRef.current?.focus(), 20)
      return () => window.clearTimeout(t)
    }
    return undefined
  }, [dialog])

  const portalTarget = typeof document !== "undefined" ? document.body : null

  return (
    <>
      {children}
      {portalTarget
        ? createPortal(
            <>
              {dialog ? (
                <DialogCard
                  title={dialog.title}
                  message={dialog.message}
                  onBackdrop={() => closeDialog(dialog.kind === "confirm" ? false : null)}
                >
                  {dialog.kind === "prompt" ? (
                    <label className="mt-3 grid gap-1 text-xs" htmlFor={inputId}>
                      <span className="text-muted-foreground">Value</span>
                      <input
                        id={inputId}
                        ref={inputRef}
                        defaultValue={dialog.defaultValue || ""}
                        placeholder={dialog.placeholder || ""}
                        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-ring/20"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault()
                            closeDialog(e.currentTarget.value)
                          }
                        }}
                      />
                    </label>
                  ) : null}
                  <div className="mt-4 flex justify-end gap-2">
                    {dialog.kind !== "alert" ? (
                      <Button type="button" variant="secondary" onClick={() => closeDialog(dialog.kind === "confirm" ? false : null)}>
                        {dialog.cancelLabel || "Cancel"}
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant={dialog.danger ? "default" : "default"}
                      className={dialog.danger ? "bg-red-600 text-white hover:bg-red-600/90" : undefined}
                      onClick={() => {
                        if (dialog.kind === "confirm") closeDialog(true)
                        else if (dialog.kind === "prompt") closeDialog(inputRef.current?.value ?? "")
                        else closeDialog(undefined)
                      }}
                    >
                      {dialog.confirmLabel || "OK"}
                    </Button>
                  </div>
                </DialogCard>
              ) : null}

              <div className="pointer-events-none fixed right-3 top-3 z-[210] flex w-[min(420px,calc(100vw-24px))] flex-col gap-2">
                {toasts.map((toast) => (
                  <div
                    key={toast.id}
                    role="status"
                    className={cn(
                      "pointer-events-auto flex items-start gap-2 rounded-lg border bg-card px-3 py-2.5 text-sm text-foreground shadow-lg",
                      toast.type === "alert" ? "border-red-300" : "border-emerald-300"
                    )}
                  >
                    <div className="min-w-0 flex-1 whitespace-pre-wrap leading-snug">{toast.message}</div>
                    <button
                      type="button"
                      className="rounded px-1.5 text-lg leading-none text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label="Dismiss"
                      onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </>,
            portalTarget
          )
        : null}
    </>
  )
}
